import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout, FieldGrid,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Trading → Raise / Invest Cash → Raise Cash.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version 1, activated 2026-10-04): screens "Raise Cash Details" and "Raise
 * Cash Accounts". Choosing "Move to Distribution Sleeve (We need Operations to
 * Process a Distribution)" also creates an Operations case (Distributions /
 * Move Money → Distribution (One-Time)) as a child of the Trading case.
 * Invest Cash shares the category but is a different task with different
 * screens, so it has its own page (InvestCash.js). Shared chrome and policy
 * copy live in GuideKit.js.
 */

const OPS_OPTION = 'Move to Distribution Sleeve (We need Operations to Process a Distribution)';

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Raise / Invest Cash',
    subCategory: 'Raise Cash',
    subject: subjectRow('Raise cash - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which sleeves you can pick later, and the advisor and office are taken from it.']],
  }),
  {
    title: 'Answer the Raise Cash Details questions',
    body: (
      <p style={{ margin: 0 }}>
        Questions reveal themselves one at a time based on your answers, so you only see what applies. Every
        question in the reference below is required once it appears. The last one, <em>Please enter any other
        details the Trading Team may need...</em>, becomes the case description the Trading team reads first.
      </p>
    ),
  },
  {
    title: 'Pick the sleeve to raise cash from',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>Raise Cash Accounts</strong> screen shows one table, <strong>Select Accounts (Raise
        Cash)</strong>, with Account Number, Account Details, Model, Cash Balance and Balance columns and a search
        bar. Pick exactly one row - the sleeve the cash is raised from. If cash is coming from more than one
        sleeve, say so in the details box on the previous screen.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the table - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question on the two screens, in the order it can appear.
const QUESTIONS = [
  {
    screen: 'Raise Cash Details',
    q: 'Is this for a Qualified or Non-Qualified account?',
    when: 'Always - this is the first question',
    options: ['Qualified', 'Non-Qualified'],
  },
  {
    q: 'Do you wish to add a Capital Gains budget? (Recommended to avoid adverse tax consequences for the Client)',
    when: 'Only if you chose Non-Qualified',
    options: ['Yes (I wish to add a Capital Gains Budget)', 'No (I do not wish to add a Capital Gains Budget)'],
    note: 'Answering No goes straight to the funds question below.',
  },
  {
    q: 'Max Long Term Gains ($) · Max Short Term Gains ($)',
    when: 'Only if you chose to add a Capital Gains budget',
    note: 'Dollar amounts, not percentages. Both are saved on the case.',
  },
  {
    q: 'Capital Gains Details',
    when: 'Only if you chose to add a Capital Gains budget',
    note: 'Saved on the case as the capital gains notes. The next question appears once this has something in it.',
  },
  {
    q: 'What would you like to do with the funds once they are raised?',
    when: 'If the account is Qualified, if you chose not to add a budget, or once Capital Gains Details is filled in',
    options: [
      OPS_OPTION,
      'Move to a Distribution Sleeve (Leave there, we do NOT need Operations to Process a Distribution)',
      'Move to a Cash Only Sleeve (Leave as Cash)',
      'Move to a Cash Only Sleeve (Buy to Money Market first!)',
      "Leave it where it is (We'll let you know what to do with it when we are ready)",
      'Journal to another sleeve / Other',
    ],
    note: <>The first option also opens an Operations distribution case for you when you submit - see <em>What happens next</em>. The others are Trading only.</>,
  },
  {
    q: 'Raise Cash Amount',
    when: 'Once you have chosen what to do with the funds',
    note: 'Recorded as the amount on the case.',
  },
  {
    q: 'You answered "Yes" to have Ops send a Distribution and that the account is "Qualified" - would you like to withhold taxes?',
    when: `Only if the account is Qualified, you chose ${OPS_OPTION}, and the amount is more than $10`,
    options: ['Yes (Withhold Taxes)', 'No (Do Not Withhold Taxes)'],
  },
  {
    q: 'Federal Tax Withheld · State Tax Withheld',
    when: 'Only if you chose to withhold taxes',
    note: 'Enter each as a percentage between 0 and 99.99 - 10 means 10%, not $10. Once either is above 0 the form shows the Net Distribution: the amount less both percentages. Check it against what you told the client.',
  },
  {
    q: 'Please enter any other details the Trading Team may need...',
    when: `Once the amount is entered. If Operations is processing a distribution from a Qualified account, it appears after the withholding question instead`,
    note: 'Becomes the case description. Include anything the answers above do not cover - several sleeves, specific holdings to sell, timing. When an Operations case is opened, the same text becomes its description too.',
  },
  {
    screen: 'Raise Cash Accounts',
    q: 'Select Accounts (Raise Cash)',
    when: 'Always',
    note: 'Single select - pick exactly one sleeve. Columns: Account Number, Account Details, Model, Cash Balance, Balance.',
  },
  {
    q: 'Acknowledgement',
    when: 'Always, under the Operations and Trading notice',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Raise / Invest Cash'),
  {
    h: "The next question hasn't appeared",
    p: [
      'Each question waits for the one before it. With a Capital Gains budget, the funds question waits until Capital Gains Details has something in it; Raise Cash Amount waits for the funds question; the details box waits for the amount.',
    ],
  },
  {
    h: "I wasn't asked about tax withholding",
    p: [
      `The withholding question only appears when all three are true: the account is Qualified, you chose ${OPS_OPTION}, and the amount is more than $10.`,
    ],
  },
  troubleSleeveRows('The table lists the sleeves of the household chosen on the first screen.'),
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function RaiseCash() {
  return (
    <GuidePage
      title="Raise Cash Requests"
      icon="cash-coin"
      path="Trading → Raise / Invest Cash → Raise Cash"
      minutes={5}
      intro="How to ask the Prosperity Trading team to raise cash in a client sleeve - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers.</Para>
        <Bullets items={[
          <><strong>The sleeve the cash is coming from</strong>, under the client&rsquo;s household. You pick it from a list, so you do not need the number memorised.</>,
          <><strong>Whether the account is Qualified or Non-Qualified.</strong></>,
          <><strong>For a Non-Qualified account, whether the client wants a Capital Gains budget</strong> - and if so the maximum long-term and short-term gains in dollars, plus any notes for the team.</>,
          <><strong>What should happen to the cash once it is raised</strong> - distribution sleeve, cash only sleeve, money market, leave it, or journal elsewhere.</>,
          <><strong>The amount to raise.</strong></>,
          <><strong>The tax withholding decision</strong>, with federal and state percentages, if Operations is sending a distribution from a Qualified account.</>,
        ]} />
        <Callout>
          Choosing <em>{OPS_OPTION}</em> makes one submission produce two cases: this Trading case, and an Operations
          distribution case linked to it.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="trading"
        after={(
          <div style={{ marginTop: '1rem' }}>
            <Para>
              <strong>If you chose {OPS_OPTION}</strong>, the flow also creates an Operations case straight after
              the Trading case, linked to it as its parent. It is set up as:
            </Para>
            <FieldGrid rows={[
              ['Category', 'Distributions / Move Money'],
              ['Sub-Category', 'Distribution (One-Time)'],
              ['Subject and Due Date', 'The same as the Trading case.'],
              ['Copied across', 'The household, advisor and office, the sleeve you picked, the Raise Cash Amount, the federal and state withholding, the Net Distribution, and your details text as its description.'],
            ]} />
          </div>
        )}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not tax advice, and it does not describe how the
        Trading team chooses what to sell or the tax consequences for the client.
      </Footnote>
    </GuidePage>
  );
}
